import { Icon, Input } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IconType } from "react-icons";
import {
    TbAddressBook,
    TbAdjustments,
    TbAlertTriangle,
    TbBuilding,
    TbChartBar,
    TbClipboardText,
    TbCoin,
    TbDownload,
    TbFileText,
    TbMessages,
    TbPhoneCall,
    TbRobot,
    TbSearch,
    TbSparkles,
} from "react-icons/tb";

import { Link as AppLink } from "@/components/link";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

export const Route = createFileRoute(APP_ROUTES_FULL.DEVELOPER_INDEX)({
    component: DeveloperIndexPage,
});

const AI_EXPERIMENTS = [
    {
        id: "dimension-definitions",
        titleKey: "dimension_definitions",
        descriptionKey: "developer_dimension_definitions_description",
        to: APP_ROUTES_TO.AI_DIMENSION_DEFINITIONS,
        icon: TbClipboardText,
    },
    {
        id: "dimensions",
        titleKey: "dimensions",
        descriptionKey: "developer_dimensions_description",
        to: APP_ROUTES_TO.AI_DIMENSIONS,
        icon: TbAdjustments,
    },
    {
        id: "analysers",
        titleKey: "developer_analysers_title",
        descriptionKey: "developer_analysers_description",
        to: APP_ROUTES_TO.DEVELOPER_ANALYSERS,
        icon: TbSparkles,
    },
] as const;

const DARWIN_PAGES = [
    {
        id: "conversations",
        titleKey: "conversations",
        descriptionKey: "developer_darwin_conversations_description",
        to: APP_ROUTES_TO.DEVELOPER_DARWIN_CONVERSATIONS,
        icon: TbMessages,
    },
    {
        id: "cost",
        titleKey: "developer_darwin_cost_title",
        descriptionKey: "developer_darwin_cost_description",
        to: APP_ROUTES_TO.DEVELOPER_DARWIN_COST,
        icon: TbCoin,
    },
] as const;

const OPERATIONS_EXPERIMENTS = [
    {
        id: "kpis-report",
        titleKey: "developer_kpis_report_title",
        descriptionKey: "developer_kpis_report_description",
        to: APP_ROUTES_TO.DEVELOPER_KPIS_REPORT,
        icon: TbChartBar,
    },
    {
        id: "bulk-call-actions",
        titleKey: "developer_bulk_call_actions_title",
        descriptionKey: "developer_bulk_call_actions_description",
        to: APP_ROUTES_TO.DEVELOPER_BULK_CALL_ACTIONS,
        icon: TbSparkles,
    },
    {
        id: "calls-by-phone",
        titleKey: "developer_calls_by_phone_title",
        descriptionKey: "developer_calls_by_phone_description",
        to: APP_ROUTES_TO.DEVELOPER_CALLS_BY_PHONE,
        icon: TbPhoneCall,
    },
    {
        id: "telli",
        titleKey: "developer_telli_title",
        descriptionKey: "developer_telli_description",
        to: APP_ROUTES_TO.DEVELOPER_TELLI,
        icon: TbRobot,
    },
    {
        id: "contacts",
        titleKey: "developer_contacts_title",
        descriptionKey: "developer_contacts_description",
        to: APP_ROUTES_TO.DEVELOPER_CONTACTS,
        icon: TbAddressBook,
    },
] as const;

const INCIDENTS_EXPERIMENTS = [
    {
        id: "incidents",
        titleKey: "incidents",
        descriptionKey: "developer_incidents_description",
        to: APP_ROUTES_TO.INCIDENTS,
        icon: TbAlertTriangle,
    },
] as const;

const DATA_EXPORTS_EXPERIMENTS = [
    {
        id: "data-exports",
        titleKey: "data_exports",
        descriptionKey: "developer_data_exports_description",
        to: APP_ROUTES_TO.DEVELOPER_DATA_EXPORTS,
        icon: TbDownload,
    },
] as const;

const EXPERIMENTS_EXPERIMENTS = [
    {
        id: "custom-pages",
        titleKey: "custom_pages",
        descriptionKey: "developer_custom_pages_description",
        to: APP_ROUTES_TO.MANAGEMENT_CUSTOM_PAGES,
        icon: TbFileText,
    },
    {
        id: "office",
        titleKey: "office",
        descriptionKey: "developer_office_description",
        to: APP_ROUTES_TO.MANAGEMENT_OFFICE,
        icon: TbBuilding,
    },
] as const;

const SECTIONS = [
    {
        id: "ai",
        titleKey: "ai",
        items: AI_EXPERIMENTS,
        subsections: [{ id: "darwin", titleKey: "darwin", items: DARWIN_PAGES }],
    },
    { id: "incidents", titleKey: "incidents", items: INCIDENTS_EXPERIMENTS },
    { id: "data-exports", titleKey: "data_exports", items: DATA_EXPORTS_EXPERIMENTS },
    { id: "experiments", titleKey: "developer_experiments", items: EXPERIMENTS_EXPERIMENTS },
    { id: "operations", titleKey: "developer_operations", items: OPERATIONS_EXPERIMENTS },
] as const;

function ExperimentCard({
    experiment,
}: {
    experiment: {
        id: string;
        titleKey: string;
        descriptionKey: string;
        to: (typeof APP_ROUTES_TO)[keyof typeof APP_ROUTES_TO];
        icon: IconType;
    };
}) {
    const { t } = useTranslation();
    const cardIcon = experiment.icon;

    return (
        <AppLink
            to={experiment.to}
            className="group flex h-full flex-col gap-3 rounded-xl border border-border/70 bg-card/95 p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:bg-card hover:shadow-md hover:shadow-primary/10"
        >
            <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
                    <Icon
                        icon={cardIcon}
                        size={20}
                        className="transition-transform duration-200 group-hover:scale-105"
                    />
                </div>
                <h3 className="font-semibold tracking-tight transition-colors group-hover:text-primary">
                    {t(experiment.titleKey)}
                </h3>
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">{t(experiment.descriptionKey)}</p>
        </AppLink>
    );
}

function DeveloperIndexPage() {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");

    const filteredSections = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        const matches = (exp: { titleKey: string; descriptionKey: string }) => {
            if (!query) return true;
            return t(exp.titleKey).toLowerCase().includes(query) || t(exp.descriptionKey).toLowerCase().includes(query);
        };

        return SECTIONS.map((section) => {
            const subsections =
                "subsections" in section
                    ? section.subsections
                          .map((subsection) => {
                              const titleMatches =
                                  query.length > 0 && t(subsection.titleKey).toLowerCase().includes(query);
                              return {
                                  ...subsection,
                                  items: titleMatches ? subsection.items : subsection.items.filter(matches),
                              };
                          })
                          .filter((subsection) => subsection.items.length > 0)
                    : [];

            return {
                id: section.id,
                titleKey: section.titleKey,
                items: section.items.filter(matches),
                subsections,
            };
        }).filter((section) => section.items.length > 0 || section.subsections.length > 0);
    }, [searchTerm, t]);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex justify-center">
                <div className="relative w-full max-w-xl">
                    <Icon
                        icon={TbSearch}
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />
                    <Input
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder={t("search")}
                        className="h-10 rounded-xl border-border/70 pl-10"
                    />
                </div>
            </div>

            {filteredSections.map((section) => (
                <section key={section.id} className="space-y-3">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                        {t(section.titleKey)}
                    </h2>
                    {section.items.length > 0 ? (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {section.items.map((experiment) => (
                                <ExperimentCard key={experiment.id} experiment={experiment} />
                            ))}
                        </div>
                    ) : null}
                    {section.subsections.map((subsection) => (
                        <div key={subsection.id} className="space-y-3 pt-2">
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                {t(subsection.titleKey)}
                            </h3>
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {subsection.items.map((experiment) => (
                                    <ExperimentCard key={experiment.id} experiment={experiment} />
                                ))}
                            </div>
                        </div>
                    ))}
                </section>
            ))}

            {filteredSections.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border/70 bg-muted/20 px-4 py-8 text-center text-sm text-muted-foreground">
                    {t("no_entities_found")}
                </div>
            ) : null}
        </div>
    );
}
